import { Layout, Table, Input, Modal } from "antd";
import { Content } from "antd/es/layout/layout";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import Swal from "sweetalert2";

import { ReactComponent as ICON_ARROW_BACK_MENU } from "@assets/svgs/ICON_ARROW_BACK_MENU.svg";
import { Button } from "@components/Button";
import Header from "@components/Header";
import { Icon } from "@components/Icon";
import { InnerContents } from "@components/InnerContents";
import { SideMenu } from "@components/SideMenu";
import { Text } from "@components/Text";
import { useSocketContext } from "@contexts/socket/socketContext";
import { setLastedForceSync } from "@redux/reducers/lastedForceSync.reducers";
import { COLOR_GREEN, COLOR_GREY, COLOR_WHITE } from "@static/COLORS";
import { CONTENT_STYLES } from "@static/CONTENT_STYLES";
import { KEY_DEVICE_MANAGEMENT_DEVICES_ADD } from "@static/PERMISSION_KEY";
import { FILTER_SIZE, MENU_SIZE, MODAL_SIZE, PAGE_SIZE } from "@static/SIZE";
import { getDeviceGroupTableColumns } from "@static/TABLE/BASE_DEVICE_GROUP_LIST_TABLE_COLUMNS";
import { getAuthUserPermissions } from "@utils/getAuthUserPermissions";
import { getAuthUserProfile } from "@utils/getAuthUserProfile";
import { fetchAPI } from "@utils/https/fetchAPI";
import { useShowConfirmation } from "@utils/useShowConfirmation";

import { FormLink } from "../ImportantListPage/FormLink";

import { FormCreate } from "./FormCreate";
import { FormEdit } from "./FormEdit";

const SectionCreate = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const SectionMainAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${COLOR_WHITE};
  padding: 8px;
  border-radius: 6px;
  height: 50px;
  box-sizing: border-box;
`;

const SectionLocationTextContainer = styled.div`
  margin-right: 8px;
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 4px;
`;

const Line = styled.div`
  height: 20px;
  width: 1px;
  background: ${COLOR_GREY};
`;

export default () => {
  const dispatch = useDispatch();
  const socket = useSocketContext();
  const showConfirmation = useShowConfirmation();
  const navigate = useNavigate();
  const location = useLocation();
  const permission = getAuthUserPermissions();

  const lastedForceSync = useSelector(({ lastedForceSync }) => {
    return _.get(lastedForceSync, ["data"]);
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filterText, setFilterText] = useState("");
  const [dataList, setDataList] = useState([]);

  const [devices, setDevices] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [allDevices, setAllDevices] = useState([]);

  const [campaignsDuration, setCampaignsDuration] = useState(null);

  const [deviceNotUsed, setDeviceNotUsed] = useState([]);

  const [selectedData, setSelectedData] = useState(null);

  const customerId = _.get(location, ["state", "customerId"]);
  const locationId = _.get(location, ["state", "locationId"]);
  const locationName = _.get(location, ["state", "locationName"]);

  const [isModalVisible, setIsModalVisible] = useState({
    isOpen: false,
    modalType: null,
  });
  const authUserProfile = getAuthUserProfile();
  // const user_type = _.get(authUserProfile, ["user_type"]);

  const onHandleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const onHandleFilterChange = (e) => {
    const value = _.get(e, ["target", "value"]);
    setFilterText(value);
  };

  const onHandleCreate = ({ modalType }) => {
    setIsModalVisible({ isOpen: true, modalType });
  };

  const onHandleEdit = ({ item }) => {
    setSelectedData(item);
    setIsModalVisible({ isOpen: true, modalType: "edit" });
  };

  const onHandleDelete = async ({ item }) => {
    const id = _.get(item, ["id"]);
    const resp = await fetchAPI({
      method: "delete",
      path: `/api/v1/device-groups/${id}`,
      isAuth: true,
    });
    if (_.get(resp, ["status"])) {
      toast.success("Delete Device Success.");
      const updateDataList = _.chain(dataList)
        .filter((item) => {
          return _.get(item, ["id"]) !== id;
        })
        .map((item, index) => {
          return { ...item, index: index + 1 };
        })
        .value();

      const updateDeviceNotUsed = _.chain(item)
        .get(["deviceGroupsDetails"])
        .map((groupItem, groupIndex) => {
          return _.get(groupItem, ["device_id"]);
        })
        .uniq()
        .thru((listId) => {
          return _.difference(deviceNotUsed, listId);
        })
        .value();
      setDeviceNotUsed(updateDeviceNotUsed);
      setDataList(updateDataList);
    }
  };

  const filteredDataSource = _.filter(dataList, (item) => {
    const itemName = _.chain(item).get(["name"]).toLower().value();
    const lowerFilterText = _.toLower(filterText);
    return _.includes(itemName, lowerFilterText);
  });

  const initData = async () => {
    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/device-groups",
      isAuth: true,
      data: { location_id: locationId },
    });
    if (_.get(resp, ["status"])) {
      const deviceGroups = _.chain(resp)
        .get(["result", "deviceGroups"])
        .map((item, index) => {
          return { key: index, index: index + 1, ...item };
        })
        .value();

      const deviceNotUsed = _.get(resp, ["result", "deviceNotUsed"]);
      const campaigns = _.chain(resp)
        .get(["result", "adsCampaigns"])
        .map((item, index) => {
          return { label: _.get(item, ["name"]), value: _.get(item, ["id"]) };
        })
        .value();
      const devices = _.chain(resp)
        .get(["result", "devices"])
        .filter((item, index) => {
          const deviceId = _.get(item, ["id"]);
          return !_.includes(deviceNotUsed, deviceId);
        })
        .map((item, index) => {
          return { label: _.get(item, ["name"]), value: _.get(item, ["id"]) };
        })
        .value();

      const allDevices = _.chain(resp)
        .get(["result", "devices"])
        .map((item, index) => {
          return { label: _.get(item, ["name"]), value: _.get(item, ["id"]) };
        })
        .value();

      const currentDate = dayjs();

      const campaignMaxDuration = _.chain(resp)
        .get(["result", "adsCampaigns"])
        .map((campaign) => {
          const maxDuration = _.chain(campaign)
            .get(["adsBlocks"])
            .map((adsBlock) => {
              const filteredBlockMedias = _.chain(adsBlock)
                .get(["blockMedias"])
                .filter((media) => {
                  const endDate = _.get(media, ["end_date"]);
                  return dayjs(endDate).isAfter(currentDate);
                })
                .value();

              return _.sumBy(filteredBlockMedias, "duration");
            })
            .max()
            .value();

          return {
            campaignId: _.get(campaign, ["id"]),
            maxDuration,
          };
        })
        .keyBy("campaignId")
        .mapValues("maxDuration")
        .value();
      // key = campaignId value = maxDuration

      setCampaignsDuration(campaignMaxDuration);

      setDeviceNotUsed(deviceNotUsed);
      setAllDevices(allDevices);
      setDevices(devices);
      setCampaigns(campaigns);
      setDataList(deviceGroups);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const onHandleSoftSycGroup = ({ item }) => {
    const playerList = _.chain(item)
      .get(["deviceGroupsDetails"])
      .map((deviceDetails) => {
        return _.get(deviceDetails, ["device", "player_id"]);
      })
      .uniq()
      .value();

    socket.emit("DEVICE-GROUP-SEND-SOFT-SYNCHRONIZED-TO-SERVICE", {
      playerList,
    });
    toast.success("Soft Sync Success.");
  };

  // const onHandleForceSycGroup = ({ item }) => {
  //   console.log("item :>> ", item);
  //   console.log("lastedForceSync :>> ", lastedForceSync);
  //   const campaignId = _.get(item, ["id"]);
  //   const pressTime = dayjs().add(30, "minute").unix();
  //   const campaignPress = { [campaignId]: pressTime };
  //   console.log("campaignPress :>> ", campaignPress);
  //   // const playerList = _.chain(item)
  //   //   .get(["deviceGroupsDetails"])
  //   //   .map((deviceDetails) => {
  //   //     return _.get(deviceDetails, ["device", "player_id"]);
  //   //   })
  //   //   .uniq()
  //   //   .value();
  //   // // const chanel = _.get(item, ["id"]);
  //   // socket.emit("DEVICE-GROUP-SEND-FORCE-SYNCHRONIZED-TO-SERVICE", {
  //   //   playerList,
  //   // });
  //   // // //
  //   // toast.success("Force Sync Success.");
  // };

  const onHandleForceSycGroup = async ({ item }) => {
    const campaignId = _.get(item, ["id"]);
    const now = dayjs();
    const previousPressTime = _.get(lastedForceSync, [campaignId]);

    const updatePressTime = dayjs().add(1, "minute").unix();
    const campaignPress = { [campaignId]: updatePressTime };
    const updatedForceSync = {
      ...lastedForceSync,
      ...campaignPress,
    };
    dispatch(setLastedForceSync(updatedForceSync));
    const playerList = _.chain(item)
      .get(["deviceGroupsDetails"])
      .map((deviceDetails) => {
        return _.get(deviceDetails, ["device", "player_id"]);
      })
      .uniq()
      .value();
    console.log(
      "playerList :>> ",
      _.chain(playerList).compact().isEmpty().value()
    );
    if (!_.chain(playerList).compact().isEmpty().value()) {
      console.log("playerList :>> ", playerList);
      const result = await fetchAPI({
        method: "post",
        path: "/api/v1/back-office-update-download-status",
        isAuth: true,
        data: { player_ids: playerList },
      });

      if (result?.status) {
        socket.emit("DEVICE-GROUP-SEND-FORCE-SYNCHRONIZED-TO-SERVICE", {
          playerList,
        });
        toast.success("Force Sync Success.");
      }
    } else {
      toast.success("No player in group");
    }
  };

  return (
    <Layout hasSider>
      <SideMenu />
      <Layout style={{ marginLeft: MENU_SIZE }}>
        <Header />
        <Content style={CONTENT_STYLES}>
          <InnerContents>
            <SectionMainAction>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <Button onClick={() => navigate(-1)} padding="0px">
                  <Icon
                    isCursor
                    width={14}
                    height={14}
                    style={{ marginRight: 8 }}
                  >
                    <ICON_ARROW_BACK_MENU />
                  </Icon>
                  <Text fontSize="18px" fontWeight="600">
                    Back
                  </Text>
                </Button>
                <Line />

                <Input
                  placeholder="Filter by name"
                  value={filterText}
                  onChange={(e) => onHandleFilterChange(e)}
                  style={{ width: FILTER_SIZE }}
                />
              </div>

              <SectionCreate>
                <SectionLocationTextContainer>
                  <Text
                    fontSize="16px"
                    fontWeight="400"
                    color="rgb(150 150 150)"
                  >
                    Project
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="400"
                    color="rgb(150 150 150)"
                  >
                    :
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="500"
                    isEllipsis
                    lines={1}
                    isBreak
                  >
                    {locationName}
                  </Text>
                </SectionLocationTextContainer>

                {_.includes(permission, KEY_DEVICE_MANAGEMENT_DEVICES_ADD) && (
                  <Button
                    onClick={() => onHandleCreate({ modalType: "create" })}
                    color={COLOR_GREEN}
                    fontColor={COLOR_WHITE}
                    padding="7px 15px"
                  >
                    Add Device Group
                  </Button>
                )}
              </SectionCreate>
            </SectionMainAction>

            <Table
              dataSource={filteredDataSource}
              columns={getDeviceGroupTableColumns({
                permission,
                onHandleEdit,
                onHandleDelete,
                showConfirmation,
                onHandleSoftSycGroup,
                onHandleForceSycGroup,
                // onHandleLinkDevices,
              })}
              pagination={{
                position: ["bottomRight"],
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
              }}
              // scroll={{ x: 1200 }}
              onChange={onHandleTableChange}
            />
          </InnerContents>
        </Content>
      </Layout>
      <Modal
        width={MODAL_SIZE}
        destroyOnClose={true}
        title={`${_.chain(isModalVisible)
          .get(["modalType"])
          .capitalize()
          .value()} Device Group`}
        open={_.get(isModalVisible, ["isOpen"])}
        footer={null}
        onCancel={() =>
          setIsModalVisible((prev) => ({ ...prev, isOpen: false }))
        }
      >
        {_.get(isModalVisible, ["modalType"]) === "create" && (
          <FormCreate
            customerId={customerId}
            locationId={locationId}
            userProfile={authUserProfile}
            deviceNotUsed={deviceNotUsed}
            // devices={devices}
            campaigns={campaigns}
            allDevices={allDevices}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setDeviceNotUsed={setDeviceNotUsed}
            dataList={dataList}
          />
        )}
        {_.get(isModalVisible, ["modalType"]) === "edit" && (
          <FormEdit
            customerId={customerId}
            locationId={locationId}
            allDevices={allDevices}
            campaignsDuration={campaignsDuration}
            devices={devices}
            deviceNotUsed={deviceNotUsed}
            campaigns={campaigns}
            userProfile={authUserProfile}
            selectedData={selectedData}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setDeviceNotUsed={setDeviceNotUsed}
            setSelectedData={setSelectedData}
          />
        )}
        {/* {_.get(isModalVisible, ["modalType"]) === "link" && (
          // <FormEdit
          //   customerId={customerId}
          //   locationId={locationId}
          //   // allDevices={allDevices}
          //   // campaignsDuration={campaignsDuration}
          //   // devices={devices}
          //   // deviceNotUsed={deviceNotUsed}
          //   // campaigns={campaigns}
          //   // userProfile={authUserProfile}
          //   // selectedData={selectedData}
          //   // setIsModalVisible={setIsModalVisible}
          //   // setDataList={setDataList}
          //   // setDeviceNotUsed={setDeviceNotUsed}
          //   // setSelectedData={setSelectedData}
          // />
          <FormLink
            selectedData={selectedData}
            customerId={customerId}
            locationId={locationId}
          />
        )} */}
      </Modal>
    </Layout>
  );
};
